import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card } from '@/components/ui';
import { inputClass } from '@/components/forms/form';
import ServiceIcon, { BookingTypeIcon, categoryTint } from '@/components/companion/ServiceIcon';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import {
  useServiceCategories,
  useBands,
  bookingTypeLabel,
  unitLabel,
  type Band,
  type BookingType,
  type ServiceCategory,
} from '@/lib/data/services';

/**
 * Where a companion chooses what they offer and what they charge.
 *
 * A rate is per (service, booking type) — the same person can be 12 coins/min
 * on a video call and 400 coins/hour across a dinner table, for the same
 * service. See docs/11-SPEC-CHECK.md §3.
 *
 * Writes go straight to the database rather than through the API. That is safe
 * because the price band and the allowed channels are enforced by a trigger
 * inside Postgres (migration 0012), not by this form.
 */
interface Listing {
  id: string;
  category_id: string;
  headline: string | null;
  is_active: boolean;
}

interface RateRow {
  id: string;
  companion_service_id: string;
  booking_type: BookingType;
  price_coins: number;
}

export default function CompanionServices() {
  const { profile } = useAuth();
  const { categories, loading: loadingCats } = useServiceCategories();
  const bands = useBands();

  const [listings, setListings] = useState<Listing[]>([]);
  const [rates, setRates] = useState<RateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!profile) return;

    const { data: ls, error: e1 } = await supabase
      .from('companion_services')
      .select('id, category_id, headline, is_active')
      .eq('companion_id', profile.id);

    if (e1) {
      setError(e1.message);
      setLoading(false);
      return;
    }

    const listingRows = (ls as unknown as Listing[]) ?? [];
    setListings(listingRows);

    if (listingRows.length > 0) {
      const { data: rs } = await supabase
        .from('companion_service_rates')
        .select('id, companion_service_id, booking_type, price_coins')
        .in(
          'companion_service_id',
          listingRows.map((l) => l.id),
        );
      setRates((rs as unknown as RateRow[]) ?? []);
    } else {
      setRates([]);
    }

    setLoading(false);
  }, [profile]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!profile) return null;

  const byCategory = new Map(listings.map((l) => [l.category_id, l]));

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <header className="animate-rise mb-8">
        <p className="eyebrow mb-2">Your listing</p>
        <h1 className="text-3xl font-semibold">Services you offer</h1>
        <p className="mt-2 text-[var(--ink-2)]">
          Add a service, then set a price for each way someone can book it. Calls are priced per
          minute; meeting in person is priced per hour.
        </p>
      </header>

      {error && (
        <Card className="mb-6 px-5 py-4">
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        </Card>
      )}

      {loading || loadingCats ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-2xl"
              style={{ background: 'var(--surface-2)' }}
            />
          ))}
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {categories.map((c) => {
            const listing = byCategory.get(c.id);
            return (
              <ServiceRow
                key={c.id}
                category={c}
                listing={listing}
                rates={rates.filter((r) => r.companion_service_id === listing?.id)}
                bands={bands}
                companionId={profile.id}
                onChanged={load}
                onError={setError}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ServiceRow({
  category,
  listing,
  rates,
  bands,
  companionId,
  onChanged,
  onError,
}: {
  category: ServiceCategory;
  listing: Listing | undefined;
  rates: RateRow[];
  bands: Record<string, Band>;
  companionId: string;
  onChanged: () => Promise<void>;
  onError: (m: string | null) => void;
}) {
  const isOn = Boolean(listing);
  const [open, setOpen] = useState(false);
  const [headline, setHeadline] = useState(listing?.headline ?? '');
  const [busy, setBusy] = useState(false);

  const tint = categoryTint(category.hue);

  async function addService() {
    setBusy(true);
    onError(null);

    const { error } = await supabase.from('companion_services').insert({
      companion_id: companionId,
      category_id: category.id,
      headline: headline.trim() || null,
    });

    if (error) onError(error.message);
    else await onChanged();
    setBusy(false);
  }

  async function saveHeadline() {
    if (!listing) return;
    setBusy(true);
    const { error } = await supabase
      .from('companion_services')
      .update({ headline: headline.trim() || null })
      .eq('id', listing.id);

    if (error) onError(error.message);
    else await onChanged();
    setBusy(false);
  }

  async function removeService() {
    if (!listing) return;
    setBusy(true);
    // Rates cascade — the FK is ON DELETE CASCADE.
    const { error } = await supabase.from('companion_services').delete().eq('id', listing.id);
    if (error) onError(error.message);
    else await onChanged();
    setBusy(false);
    setOpen(false);
  }

  return (
    <Card as="li" className="overflow-hidden transition-all duration-200">
      <div className="flex items-center gap-4 p-4">
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
          style={
            isOn ? tint : { background: 'var(--surface-2)', color: 'var(--muted)' }
          }
        >
          <ServiceIcon iconKey={category.iconKey} size={22} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold">{category.name}</h2>
            {isOn && rates.length > 0 && <Badge tone="teal">Live</Badge>}
            {isOn && rates.length === 0 && <Badge tone="marigold">Needs a price</Badge>}
          </div>
          <p className="mt-0.5 text-sm text-[var(--ink-2)]">
            {rates.length > 0
              ? rates
                  .map(
                    (r) =>
                      `${bookingTypeLabel(r.booking_type)} ${r.price_coins}${unitLabel(
                        r.booking_type === 'in_person' ? 'per_hour' : 'per_minute',
                      )}`,
                  )
                  .join(' · ')
              : category.allowedBookingTypes.map(bookingTypeLabel).join(' · ')}
          </p>
        </div>

        {isOn ? (
          <Button size="sm" variant="secondary" onClick={() => setOpen(!open)}>
            {open ? 'Close' : 'Edit'}
          </Button>
        ) : (
          <Button size="sm" onClick={() => void addService()} disabled={busy}>
            Add
          </Button>
        )}
      </div>

      {open && listing && (
        <div
          className="animate-rise flex flex-col gap-5 border-t px-4 py-4"
          style={{ borderColor: 'var(--line-soft)', background: 'var(--surface-2)' }}
        >
          <p className="text-sm text-[var(--ink-2)]">{category.guidance}</p>

          <div>
            <span className="mb-2 block text-sm font-medium">Your prices</span>
            <div className="flex flex-col gap-2">
              {category.allowedBookingTypes.map((t) => (
                <RateInput
                  key={t}
                  bookingType={t}
                  listingId={listing.id}
                  existing={rates.find((r) => r.booking_type === t)}
                  band={bands[t]}
                  onChanged={onChanged}
                  onError={onError}
                />
              ))}
            </div>
            <p className="mt-2 text-xs text-[var(--muted)]">
              Leave a channel blank if you do not want to offer it.
            </p>
          </div>

          <label className="text-sm">
            <span className="mb-1.5 block font-medium">One line about it</span>
            <input
              className={`${inputClass} rounded-xl border`}
              style={{ background: 'var(--surface)', borderColor: 'var(--line)' }}
              value={headline}
              maxLength={120}
              placeholder="Optional"
              onChange={(e) => setHeadline(e.target.value)}
              onBlur={() => void saveHeadline()}
            />
          </label>

          <div>
            <Button size="sm" variant="ghost" onClick={() => void removeService()} disabled={busy}>
              Remove this service
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * One price, for one channel. Saves on blur rather than behind a button —
 * there can be four of these on screen and four Save buttons is noise.
 */
function RateInput({
  bookingType,
  listingId,
  existing,
  band,
  onChanged,
  onError,
}: {
  bookingType: BookingType;
  listingId: string;
  existing: RateRow | undefined;
  band: Band | undefined;
  onChanged: () => Promise<void>;
  onError: (m: string | null) => void;
}) {
  const [value, setValue] = useState(existing ? String(existing.price_coins) : '');
  const [busy, setBusy] = useState(false);

  const unit = bookingType === 'in_person' ? 'per_hour' : 'per_minute';
  const n = Number(value);
  const outOfBand = value !== '' && band !== undefined && (n < band.min || n > band.max);

  async function commit() {
    if (busy) return;
    const trimmed = value.trim();

    // Cleared — remove the rate, which is how a companion stops offering a
    // channel without removing the whole service.
    if (trimmed === '') {
      if (!existing) return;
      setBusy(true);
      const { error } = await supabase
        .from('companion_service_rates')
        .delete()
        .eq('id', existing.id);
      if (error) onError(error.message);
      else await onChanged();
      setBusy(false);
      return;
    }

    if (!Number.isFinite(n) || n <= 0 || outOfBand) return;
    if (existing && existing.price_coins === n) return;

    setBusy(true);
    onError(null);

    const { error } = existing
      ? await supabase
          .from('companion_service_rates')
          .update({ price_coins: n })
          .eq('id', existing.id)
      : await supabase.from('companion_service_rates').insert({
          companion_service_id: listingId,
          booking_type: bookingType,
          price_coins: n,
        });

    if (error) onError(error.message);
    else await onChanged();
    setBusy(false);
  }

  return (
    <div className="flex items-center gap-3">
      <span className="flex w-28 shrink-0 items-center gap-1.5 text-sm text-[var(--ink-2)]">
        <BookingTypeIcon type={bookingType} size={15} />
        {bookingTypeLabel(bookingType)}
      </span>

      <input
        type="number"
        inputMode="numeric"
        className="w-24 rounded-lg px-3 py-2 text-sm tabular-nums outline-none"
        style={{
          background: 'var(--surface)',
          color: 'var(--ink)',
          border: `1px solid ${outOfBand ? 'var(--danger)' : 'var(--line)'}`,
        }}
        value={value}
        placeholder="—"
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void commit()}
      />

      <span className="text-sm text-[var(--ink-2)]">
        coins{unitLabel(unit)}
        {band && (
          <span className="ml-2 text-xs text-[var(--muted)]">
            {band.min}–{band.max}
          </span>
        )}
      </span>
    </div>
  );
}
